import { memo } from "react";
import { FormControl } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGripVertical } from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import { RequestStatus } from "@/types/statusTypes";
import { ACTION_ICONS, EXTENDED_ACTION_LIST } from "./ActionEvent.const";
import { useActionEvent } from "./useActionEvent";

import { Hint } from "@/HOC";
import RunConditions from "./RunConditions/RunConditions";
import CategoryConditions from "./CategoryConditions/CategoryConditions";
import FormCopyAndMoveAction from "../Forms/FormCopyAndMoveAction/FormCopyAndMoveAction";
import { AlertDialog, BlockWithLabel, IconSelect } from "@/ui";
import Legend from "./Legend/Legend";
import SayPhraseOrConfirmation from "./SayPhraseOrConfirmation/SayPhraseOrConfirmation";
import SendFile from "./SendFile/SendFile";
import SetVariable from "./SetVariable/SetVariable";
import GoTo from "./GoTo/GoTo";
import SetReport from "./SetReport/SetReport";
import DeclineData from "./DeclineData/DeclineData";
import Transfer from "./Transfer/Transfer";
import BreakOrEnd from "./BreakOrEnd/BreakOrEnd";
import SayInternal from "./SayInternal/SayInternal";
import SayExternal from "./SayExternal/SayExternal";
import ConditionsSideblock from "./ConditionsSideblock/ConditionsSideblock";
import { IActionProps, TExtendedAction } from "./ActionEvent.props";
import styles from "./ActionEvent.module.scss";

const ActionEvent = ({
	action,
	idx,
	channel,
	isActionVisible,
	searchedDataElemIn,
	isFilterActive,
	actionsArr,
	changeFlgHandler,
	actionFor,
	moveAction,
}: IActionProps): JSX.Element => {
	const {
		// Состояния
		selectExtendedAction,
		numberOfNonExistentCategories,
		fieldSetRef,
		anchorElConditionsRun,
		anchorElCategoriesSettings,
		showCopyAndMoveForm,
		showAlertDialogDelAction,
		showPhraseList,
		uploadFileStatus,
		isDisabled,
		isDragging,
		handlerId,
		sesHints,

		// Сеттеры состояний
		setNumberOfNonExistentCategories,
		setAnchorElConditionsRun,
		setAnchorElCategoriesSettings,
		setShowCopyAndMoveForm,
		setShowAlertDialogDelAction,
		setShowPhraseList,

		// Обработчики
		handleExtendedActionsChange,
		handleSaveActionInStore,
		deleteActionHandler,
		drag,
		drop,

		//хелперы
		translate,
	} = useActionEvent({ action, idx, channel, isActionVisible, searchedDataElemIn, isFilterActive, actionsArr, changeFlgHandler, actionFor, moveAction });

	!isDisabled && drag(drop(fieldSetRef));

	return (
		<fieldset
			className={cn(styles.container, {
				[styles.containerActive]: anchorElConditionsRun || anchorElCategoriesSettings || showCopyAndMoveForm.isShown || showAlertDialogDelAction,
				[styles.containerOpacity]: isDragging,
				[styles.containerDisplayNone]: !isActionVisible,
			})}
		>
			<Legend
				action={action}
				actionFor={actionFor}
				idx={idx}
				channel={channel}
				isDisabled={isDisabled}
				selectExtendedAction={selectExtendedAction}
				numberOfNonExistentCategories={numberOfNonExistentCategories}
				showPhraseList={showPhraseList}
				setShowPhraseList={setShowPhraseList}
				setAnchorElCategoriesSettings={setAnchorElCategoriesSettings}
				setAnchorElConditionsRun={setAnchorElConditionsRun}
				isFilterActive={isFilterActive}
				searchedDataElemIn={searchedDataElemIn}
				setShowCopyAndMoveForm={setShowCopyAndMoveForm}
				setShowAlertDialogDelAction={setShowAlertDialogDelAction}
				actionsArr={actionsArr}
				moveAction={moveAction}
				handleSaveActionInStore={handleSaveActionInStore}
			/>

			<div className={styles.actionBlock}>
				{!isDisabled && (
					<div
						ref={fieldSetRef}
						className={styles.dragIconWrapper}
						data-handler-id={handlerId}
						onMouseDown={(e) => e.stopPropagation()}
						onTouchStart={(e) => e.stopPropagation()}
					>
						<FontAwesomeIcon onClick={(e) => e.stopPropagation()} icon={faGripVertical} size="xl" />
					</div>
				)}
				{/* действие */}
				<FormControl sx={{ marginTop: "1px", minWidth: 34, '.MuiInputLabel-root[data-shrink="false"]': { top: -8 }, ".MuiSelect-select": { paddingBlock: 0 } }}>
					<Hint hint={sesHints.hint_sesBlockActionTypes} position="left" offset={[0, -6]} hintWidth="380px">
						<IconSelect<TExtendedAction>
							disabled={
								isDisabled ||
								uploadFileStatus.status === RequestStatus.LOADING ||
								(actionFor.for === "robotEvent" && (actionFor.event === "interrupt" || actionFor.event === "answer delay"))
							}
							value={selectExtendedAction}
							onChange={handleExtendedActionsChange}
							iconList={ACTION_ICONS}
							itemsList={EXTENDED_ACTION_LIST}
							haveHint={false} // подсказки встроены, но отключены
							hintPrefix="hint_actionEvent"
						/>
					</Hint>
				</FormControl>

				{/* сказать / подтвердить */}
				{(selectExtendedAction === "say" || selectExtendedAction === "confirmation") &&
					(action.action === "say" || action.action === "confirmation") &&
					(action.type === "linear" || action.type === "random") && (
						<SayPhraseOrConfirmation
							action={action}
							idx={idx}
							channel={channel}
							isDisabled={isDisabled}
							showPhraseList={showPhraseList}
							setShowPhraseList={setShowPhraseList}
							handleSaveActionInStore={handleSaveActionInStore}
						/>
					)}

				{/* отправить файл */}
				{selectExtendedAction === "sendfile" && action.action === "sendfile" && (
					<SendFile action={action} idx={idx} channel={channel} isDisabled={isDisabled} handleSaveActionInStore={handleSaveActionInStore} />
				)}

				{/* установка переменной */}
				{selectExtendedAction === "setvariable" && action.action === "setvariable" && (
					<SetVariable action={action} idx={idx} channel={channel} isDisabled={isDisabled} handleSaveActionInStore={handleSaveActionInStore} />
				)}

				{/* принудительно перейти в КТ */}
				{selectExtendedAction === "goto" && action.action === "goto" && (
					<GoTo action={action} idx={idx} channel={channel} isDisabled={isDisabled} handleSaveActionInStore={handleSaveActionInStore} />
				)}

				{/* отправка значения в отчетность */}
				{selectExtendedAction === "setreport" && action.action === "setreport" && (
					<SetReport action={action} idx={idx} channel={channel} isDisabled={isDisabled} handleSaveActionInStore={handleSaveActionInStore} />
				)}

				{/* забыть данные */}
				{selectExtendedAction === "declinedata" && action.action === "declinedata" && (
					<DeclineData action={action} idx={idx} channel={channel} isDisabled={isDisabled} handleSaveActionInStore={handleSaveActionInStore} />
				)}

				{/* передача диалога */}
				{selectExtendedAction === "transfer" && action.action === "transfer" && (
					<Transfer action={action} idx={idx} channel={channel} isDisabled={isDisabled} handleSaveActionInStore={handleSaveActionInStore} />
				)}

				{/* остановить выполнение действий / завершить диалог */}
				{(selectExtendedAction === "break" || selectExtendedAction === "end") && (action.action === "break" || action.action === "end") && (
					<BreakOrEnd action={action} idx={idx} channel={channel} handleSaveActionInStore={handleSaveActionInStore} selectExtendedAction={selectExtendedAction} />
				)}

				{/* спросить llm / искать по документам в qas */}
				{(selectExtendedAction === "qas-chat" || selectExtendedAction === "qas-ask") && action.action === "say" && action.type === "internal" && (
					<SayInternal
						action={action}
						idx={idx}
						channel={channel}
						isDisabled={isDisabled}
						selectService={selectExtendedAction}
						handleSaveActionInStore={handleSaveActionInStore}
					/>
				)}

				{/* запустить скрипт */}
				{selectExtendedAction === "external" && action.action === "say" && action.type === "external" && (
					<SayExternal action={action} actionFor={actionFor} idx={idx} channel={channel} isDisabled={isDisabled} handleSaveActionInStore={handleSaveActionInStore} />
				)}
			</div>

			{/* превью условий */}
			{"conditions" in action && Array.isArray(action.conditions) && action.conditions.length > 0 && (
				<div className={styles.actionBlock}>
					{!isDisabled && <div className={styles.indentBlock}></div>}
					<BlockWithLabel
						label={translate("title_currentConditionsRun")}
						labelPosition="right"
						labelMarginLeft="0"
						// labelButton={
						// 	<LegendButton hint={"hint_actionEventRunConditions"} onClick={(e) => setAnchorElConditionsRun(e.currentTarget)} style={{ fontSize: "8px" }}>
						// 		{/* {translate("button_runConditions")} */}
						// 		<FontAwesomeIcon icon={faPlay} style={{ fontSize: "8px" }} />
						// 		{"conditions" in action && action.conditions && action.conditions?.length > 0 && <> ({action.conditions.length})</>}
						// 	</LegendButton>
						// }
						sx={{
							display: "flex",
							flexDirection: "column",
							width: "100%",
							padding: "3px 3px 0",
							gap: "12px",
							marginTop: "12px",
							minWidth: "0",
							backgroundColor: "rgba(0, 0, 0, 0.03)",
						}}
					>
						<ConditionsSideblock conditions={action.conditions} />
					</BlockWithLabel>
				</div>
			)}

			{/* условия запуска действий */}
			<RunConditions
				action={action}
				idx={idx}
				channel={channel}
				changeFlgHandler={changeFlgHandler}
				actionFor={actionFor}
				isDisabled={isDisabled}
				anchorElConditionsRun={anchorElConditionsRun}
				setAnchorElConditionsRun={setAnchorElConditionsRun}
				isFilterActive={isFilterActive}
			/>

			{/* условия присвоения категорий */}
			{action.action === "say" && action.type === "internal" && action.service === "qas-ask" && (
				<CategoryConditions
					action={action}
					idx={idx}
					channel={channel}
					changeFlgHandler={changeFlgHandler}
					actionFor={actionFor}
					isDisabled={isDisabled}
					anchorElCategoriesSettings={anchorElCategoriesSettings}
					setAnchorElCategoriesSettings={setAnchorElCategoriesSettings}
					isFilterActive={isFilterActive}
					setNumberOfNonExistentCategories={setNumberOfNonExistentCategories}
				/>
			)}

			{/* форма копирования/перемещения действия */}
			{showCopyAndMoveForm.isShown && (
				<FormCopyAndMoveAction
					showModal={showCopyAndMoveForm}
					setShowModal={setShowCopyAndMoveForm}
					action={action}
					actionFor={actionFor}
					sourceChannel={channel}
					sourceIdx={idx}
					changeFlgHandler={changeFlgHandler}
				/>
			)}

			{/* подтверждение удаления действия */}
			<AlertDialog
				showAlertDialog={showAlertDialogDelAction}
				setShowAlertDialog={setShowAlertDialogDelAction}
				submitHandler={deleteActionHandler}
				title="dialog_deleteAction"
				description="dialog_deleteActionConfirm"
			/>
		</fieldset>
	);
};

export default memo(ActionEvent);
